/**
 * Above / Below the Line — leadership communication view.
 * Corresponds to spec's ABOVE_BELOW_LINE output sheet.
 *
 * v23 — two lines, three bands. Harry's framing from the 2026-08-24 walkthrough:
 * "below the line for sure, above the line maybe, above the line for sure". The
 * bands come from the run's viability read model (two passes of the same pure
 * engine); the original binary striping stays available as a toggle, because
 * some rooms want one line.
 *
 * Rank distance: the gap between #1 and #2 is not the gap between #2 and #3.
 * Rows are spaced by the score deltas already carried on the ranked list, and
 * carry a magnitude bar for commitment size, so the view encodes distance and
 * not only order.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Dices, Loader2, TrendingUp, Zap } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { engineInputKindLabel, engineInstanceKey } from '../../../lib/planningEngineService';
import { usePlanningMonteCarloStore } from '../../../stores/prioritization/planningMonteCarloStore';
import { FIRM_PROBABILITY, OUT_PROBABILITY } from '../../../lib/planningMonteCarlo';
import type {
  PrioritySummary,
  DecisionWithParticipation,
  PriorityFeasibility,
  ViabilityBand,
  ViabilityBandRecord,
  ViabilityBandReadModel,
} from '../../../lib/planningEngineModule.types';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import { isAboveLine } from '../../../lib/planningViabilityBands';
import { COMMITMENT_STATE_LABELS, type CommitmentStateView } from '../../../lib/commitmentState';
import {
  computeAdmissionImpact,
  computePoolPressure,
  type PoolPressureRow,
} from '../../../lib/poolPressure';
import { cycleHasDisplacementDiscipline } from '../../../lib/displacementService';
import { useCommitmentStates } from '../../../hooks/useCommitmentStates';
import { useCommitmentStatesEnabled } from '../../../hooks/useCommitmentStatesEnabled';
import DisplacementCeremonyModal, {
  type DisplacementCandidate,
} from './DisplacementCeremonyModal';
import PoolPressureLedger from './PoolPressureLedger';
import ScenarioSummarySentence from './ScenarioSummarySentence';
import NonPlannableScopeNotice, { useScopeIsNotPlannable } from './NonPlannableScopeNotice';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function feasibilityBadge(f: PriorityFeasibility) {
  const cls =
    f === 'Fully Achievable' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
    : f === 'Partially Achievable' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
    : f === 'Not Achievable' ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
    : 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{f}</span>
  );
}

function participationBadge(d: DecisionWithParticipation) {
  if (d === 'Included') return null;
  const cls =
    d === 'At Risk - Participation' || d === 'At Risk - Capacity'
      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
      : d === 'Blocked - Capability'
        ? 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]'
        : 'bg-[var(--surface)] text-[var(--text-ter)]';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ml-1 ${cls}`}>{d}</span>
  );
}

/**
 * Announced vs backed (D1) — the missing distinction on a ladder that lets a
 * priority reach its top state with nothing bound to it. Derived, never stored.
 * `unknown` is this view's `no_basis`: resources are claimed but nothing says
 * what the work needs, so no verdict is offered.
 */
function commitmentStateBadge(view: CommitmentStateView) {
  const cls =
    view.state === 'committed'
      ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
      : view.state === 'announced'
        ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
        : 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20';
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ml-1 ${cls}`}
      title={view.sentence}
    >
      {COMMITMENT_STATE_LABELS[view.state]}
    </span>
  );
}

function reasonForBelowLine(s: PrioritySummary): string {
  if (s.feasibility === 'Not Achievable' && s.blockedOrNotReadyWU > 0) {
    return `${s.blockedOrNotReadyWU.toFixed(1)} WU blocked — hard dependency not yet Included (see Dependencies)`;
  }
  if (s.feasibility === 'Not Achievable' && s.deferredWU > 0) {
    return `${s.deferredWU.toFixed(1)} WU deferred — insufficient capacity`;
  }
  if (s.feasibility === 'Not Achievable' && s.requiredWU > 0) {
    return `${s.requiredWU.toFixed(1)} WU not achievable — capacity, dependency, or readiness`;
  }
  if (s.feasibility === 'No Work') return 'No work units defined for this priority';
  return 'Capacity, dependency, or readiness constraint';
}

// ─── Rank distance ────────────────────────────────────────────────────────────

/**
 * Extra vertical space above a row, in px, proportional to how far it sits
 * below the item ranked immediately above it. Capped so a single huge gap
 * cannot push the rest of the list off-screen; zero when the ranked set
 * carries no scores, which renders as the uniform spacing this view had before.
 */
function separationGapPx(separation: number): number {
  if (!separation || separation <= 0.05) return 0;
  return Math.round(separation * 28);
}

/** Commitment size, as a share of the largest commitment in the rendered set. */
function magnitudeShare(requiredWU: number, maxRequiredWU: number): number {
  if (maxRequiredWU <= 0 || requiredWU <= 0) return 0;
  return Math.min(1, requiredWU / maxRequiredWU);
}

// ─── Priority row ─────────────────────────────────────────────────────────────

/**
 * How often the Monte Carlo simulation carried this priority.
 *
 * The bands answer "which side of the two lines" from two passes; this answers
 * "how often" from many. It appears only once a simulation has been run for
 * this same Scenario x Input Scope — never as a stale number beside fresh ones.
 */
function ConfidenceChip({ probability }: { probability: number }) {
  const tone =
    probability >= FIRM_PROBABILITY
      ? 'text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]'
      : probability < OUT_PROBABILITY
        ? 'text-[var(--coral)] border-[var(--coral)]'
        : 'text-[var(--mustard)] border-[var(--mustard)]';
  return (
    <span
      className={`px-1.5 py-0.5 text-[11px] tabular-nums border ${tone}`}
      title={`Carried in ${Math.round(probability * 100)}% of simulated futures`}
    >
      {Math.round(probability * 100)}%
    </span>
  );
}

function PriorityRow({
  s,
  rank,
  band,
  probability,
  maxRequiredWU,
  commitmentState,
  onAskWhatComesOff,
}: {
  s: PrioritySummary;
  rank: number;
  band?: ViabilityBandRecord;
  probability?: number;
  maxRequiredWU: number;
  commitmentState?: CommitmentStateView;
  onAskWhatComesOff?: (s: PrioritySummary) => void;
}) {
  const gap = band ? separationGapPx(band.rankSeparation) : 0;
  const share = magnitudeShare(s.requiredWU, maxRequiredWU);

  return (
    <div
      className="flex items-start gap-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0"
      style={gap > 0 ? { marginTop: gap } : undefined}
    >
      <div className="w-6 text-xs text-[var(--text-ter)] tabular-nums pt-0.5 shrink-0 text-right">
        {s.priority.forceRank ?? rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-1 mb-1">
          <span className="font-medium text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {s.priority.name}
          </span>
          {feasibilityBadge(s.feasibility)}
          {probability != null && <ConfidenceChip probability={probability} />}
          {s.hasParticipationRisk && participationBadge(s.decisionWithParticipation)}
          {commitmentState && commitmentStateBadge(commitmentState)}
          {onAskWhatComesOff && (
            <button
              type="button"
              onClick={() => onAskWhatComesOff(s)}
              className="ml-1 rounded border border-[var(--rule)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
            >
              What comes off?
            </button>
          )}
        </div>
        {s.blockedReason && (
          <p className="mb-1 text-xs text-[var(--coral)]">{s.blockedReason}</p>
        )}
        <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] flex flex-wrap gap-3">
          <span className="capitalize">{s.priority.priorityResourceType}</span>
          {s.requiredWU > 0 && (
            <span>{s.includedWU.toFixed(1)} / {s.requiredWU.toFixed(1)} WU ({s.coveragePercent}% coverage)</span>
          )}
          {band
            ? /* The engine's own reason when it has one — it names the specific
                 capability, credential or person — falling back to the band's
                 own explanation of why this is not firm. */
              (band.exclusionReason ?? band.bandReason) && (
                <span className="text-[var(--mustard)] dark:text-[var(--mustard)]">
                  {band.exclusionReason ?? band.bandReason}
                </span>
              )
            : !isAboveLine(s) && (
                <span className="text-[var(--mustard)] dark:text-[var(--mustard)]">{reasonForBelowLine(s)}</span>
              )}
        </div>
      </div>
      {/* Magnitude channel — how big a commitment this row is against the set. */}
      {share > 0 && (
        <div
          className="hidden sm:block shrink-0 w-16 pt-1.5"
          title={`${s.requiredWU.toFixed(1)} WU committed`}
        >
          <div className="h-1.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--rule)] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--text-ter)]"
              style={{ width: `${Math.max(6, share * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Band section ─────────────────────────────────────────────────────────────

const BAND_STYLE: Record<
  ViabilityBand,
  { header: string; text: string; dot: string; sub: string; title: string; empty: string }
> = {
  firm: {
    header: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/10 border-[var(--green)] dark:border-[var(--green)]/30',
    text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    dot: 'bg-[var(--green)]',
    sub: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    title: 'In — firm',
    empty: 'Nothing carries fully under the conservative pass yet.',
  },
  at_risk: {
    header: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/10 border-[var(--mustard)] dark:border-[var(--mustard)]/30',
    text: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    dot: 'bg-[var(--mustard)]',
    sub: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    title: 'In — at risk',
    empty: 'Nothing sits between the two lines.',
  },
  out: {
    header: 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/10 border-[var(--coral)] dark:border-[var(--coral)]/30',
    text: 'text-[var(--coral)] dark:text-[var(--coral)]',
    dot: 'bg-[var(--coral)]',
    sub: 'text-[var(--coral)] dark:text-[var(--coral)]',
    title: 'Out',
    empty: 'Everything in scope is in.',
  },
};

function Section({
  band,
  rows,
  bandByPriorityId,
  probabilityByPriorityId,
  maxRequiredWU,
  caption,
  startRank,
  commitmentStates,
  onAskWhatComesOff,
}: {
  band: ViabilityBand;
  rows: PrioritySummary[];
  bandByPriorityId: Map<string, ViabilityBandRecord> | null;
  probabilityByPriorityId: Map<string, number> | null;
  maxRequiredWU: number;
  caption: string;
  startRank: number;
  commitmentStates?: Map<string, CommitmentStateView>;
  onAskWhatComesOff?: (s: PrioritySummary) => void;
}) {
  const style = BAND_STYLE[band];
  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className={`flex items-center gap-2 px-5 py-3 border-b ${style.header}`}>
        <div className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
        <span className={`text-sm font-semibold ${style.text}`}>{style.title}</span>
        <span className={`text-xs ml-2 ${style.sub} hidden md:inline`}>{caption}</span>
        <span className={`text-xs ml-auto ${style.sub}`}>
          {rows.length} priorit{rows.length !== 1 ? 'ies' : 'y'}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{style.empty}</p>
      ) : (
        <div className="px-5">
          {rows.map((s, i) => (
            <PriorityRow
              key={s.priority.id}
              s={s}
              rank={startRank + i}
              band={bandByPriorityId?.get(s.priority.id)}
              probability={probabilityByPriorityId?.get(s.priority.id)}
              maxRequiredWU={maxRequiredWU}
              commitmentState={commitmentStates?.get(s.priority.id)}
              onAskWhatComesOff={onAskWhatComesOff}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AboveBelowLineView() {
  const { planOutput, running, error, runEngineForScenario, activeScenario, scopedItemIds, engineInput, runBlockedReason, projectId } = usePlanningEngine();
  const [showBands, setShowBands] = useState(true);
  const scopeIsNotPlannable = useScopeIsNotPlannable();
  const commitmentStatesEnabled = useCommitmentStatesEnabled();

  const allSummaries = planOutput?.prioritySummaries ?? [];
  // Apply input-scope filter so this view stays in sync with the selected pool/collection.
  const summaries = scopedItemIds
    ? allSummaries.filter((s) => scopedItemIds.has(s.priority.id))
    : allSummaries;

  const scopeBanner = scopedItemIds && engineInput.type !== 'live'
    ? `Scoped to ${engineInputKindLabel(engineInput.type)}: ${'name' in engineInput ? engineInput.name : ''} — ${summaries.length} of ${allSummaries.length} priorities`
    : null;

  const sorted = [...summaries].sort((a, b) => {
    const ra = a.priority.forceRank ?? 9999;
    const rb = b.priority.forceRank ?? 9999;
    return ra - rb;
  });

  const maxRequiredWU = sorted.reduce((max, s) => Math.max(max, s.requiredWU), 0);

  // Bands ride the run. A run persisted before this shipped has none — the view
  // falls back to the binary striping rather than pretending it knows.
  // Confidence, when a simulation has been run for THIS instance. A probability
  // computed against another scenario or scope answers a different question, so
  // the chip disappears rather than following the user across.
  const monteCarlo = usePlanningMonteCarloStore((st) => st.result);
  const monteCarloInstanceKey = usePlanningMonteCarloStore((st) => st.instanceKey);
  const instanceKey = activeScenario ? engineInstanceKey(activeScenario.id, engineInput) : null;
  const probabilityByPriorityId = useMemo(() => {
    if (!monteCarlo || !instanceKey || monteCarloInstanceKey !== instanceKey) return null;
    return new Map(
      monteCarlo.priorityRecords.map((r) => [r.priorityId, r.inclusionProbability]),
    );
  }, [monteCarlo, monteCarloInstanceKey, instanceKey]);

  const bands: ViabilityBandReadModel | null = planOutput?.viabilityBands ?? null;
  const bandByPriorityId = bands
    ? new Map(bands.records.map((r) => [r.priorityId, r]))
    : null;
  const bandsAvailable = Boolean(bandByPriorityId);
  const showingBands = showBands && bandsAvailable;

  const bandOf = (s: PrioritySummary): ViabilityBand => {
    const record = bandByPriorityId?.get(s.priority.id);
    if (record) return record.band;
    return isAboveLine(s) ? 'firm' : 'out';
  };

  const firm = sorted.filter((s) => bandOf(s) === 'firm');
  const atRisk = sorted.filter((s) => bandOf(s) === 'at_risk');
  const out = sorted.filter((s) => bandOf(s) === 'out');

  // Binary striping — unchanged from the single-line view.
  const above = sorted.filter(isAboveLine);
  const binaryAtRisk = above.filter((s) => s.hasParticipationRisk);
  const below = sorted.filter((s) => !isAboveLine(s));

  const bufferPct = Math.round((bands?.capacityBufferPercent ?? activeScenario?.capacityBufferPercent ?? 0) * 100);

  // D1 — announced vs backed. Keyed on (plan_cycle_id, item_id): in the live
  // path s.priority.id IS items.id. Ids that are not items (the legacy
  // planning_priorities path) come back unidentified and get no badge.
  const commitments = useCommitmentStates({
    enabled: commitmentStatesEnabled,
    planCycleId: activeScenario?.planCycleId ?? null,
    itemIds: sorted.map((s) => s.priority.id),
    workspaceId: projectId,
    defaultWorkUnitHours: activeScenario?.defaultWorkUnitHours ?? 0,
  });
  const commitmentStates = commitments.available ? commitments.byItemId : undefined;
  const showCommitmentLine = Boolean(commitmentStates && commitments.rollup.total > 0);

  // D2/D3 — the pool ledger and the admission question. Capacity comes from
  // the engine's own resourceSummaries (work units), converted to the hours
  // claims speak in; the two passes mirror the engine's own two passes rather
  // than inventing a third notion of capacity.
  const wuHours = activeScenario?.defaultWorkUnitHours ?? 0;
  const bufferFraction = bands?.capacityBufferPercent ?? activeScenario?.capacityBufferPercent ?? 0;
  const demands = useMemo(
    () =>
      commitmentStates
        ? [...commitmentStates.entries()]
            .filter(([, v]) => v.requirements.length > 0)
            .map(([itemId, v]) => ({ itemId, requirements: v.requirements }))
        : [],
    [commitmentStates],
  );
  const optimisticPools: PoolPressureRow[] = useMemo(
    () =>
      computePoolPressure({
        capacities: (planOutput?.resourceSummaries ?? []).map((r) => ({
          resourceType: r.resourceType,
          capacityHours: r.capacityWU * wuHours,
        })),
        demands,
      }),
    [planOutput, wuHours, demands],
  );
  const conservativePools: PoolPressureRow[] = useMemo(
    () =>
      computePoolPressure({
        capacities: (planOutput?.resourceSummaries ?? []).map((r) => ({
          resourceType: r.resourceType,
          capacityHours: r.capacityWU * wuHours * (1 - bufferFraction),
        })),
        demands,
      }),
    [planOutput, wuHours, bufferFraction, demands],
  );
  const showPoolLedger =
    commitmentStatesEnabled && demands.length > 0 && (planOutput?.resourceSummaries?.length ?? 0) > 0;

  const nameByItemId = useMemo(
    () => new Map(sorted.map((s) => [s.priority.id, s.priority.name])),
    [sorted],
  );

  // The ceremony is offered only where the cycle has adopted the discipline —
  // it is a policy a cycle takes on at design time, not a global imposition.
  const [disciplineOn, setDisciplineOn] = useState(false);
  const cycleId = activeScenario?.planCycleId ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!commitmentStatesEnabled || !cycleId) {
      setDisciplineOn(false);
      return;
    }
    void cycleHasDisplacementDiscipline(cycleId).then((on) => {
      if (!cancelled) setDisciplineOn(on);
    });
    return () => { cancelled = true; };
  }, [commitmentStatesEnabled, cycleId]);

  const [ceremonyFor, setCeremonyFor] = useState<PrioritySummary | null>(null);
  const ceremonyState = ceremonyFor ? commitmentStates?.get(ceremonyFor.priority.id) : undefined;
  const ceremonyImpact = ceremonyState
    ? computeAdmissionImpact({
        rows: conservativePools,
        admittedRequirement: ceremonyState.requirements,
      })
    : null;
  const ceremonyCandidates: DisplacementCandidate[] = useMemo(
    () =>
      commitmentStates
        ? [...commitmentStates.entries()].map(([itemId, v]) => ({
            itemId,
            name: nameByItemId.get(itemId) ?? 'Untitled commitment',
            resourceTypes: v.requirements.map((r) => r.resourceType),
          }))
        : [],
    [commitmentStates, nameByItemId],
  );

  // Offered on commitments the record can identify, in a discipline cycle.
  const askWhatComesOff =
    disciplineOn && cycleId && commitmentStates
      ? (s: PrioritySummary) => {
          if (commitmentStates.has(s.priority.id)) setCeremonyFor(s);
        }
      : undefined;

  // Arriving from "Place a commitment" (owner ruling #12, 769):
  // ?admit=<item>&cycle=<plan cycle> opens the ceremony on that commitment
  // once the run's numbers are here, or says plainly why it cannot yet.
  const [searchParams, setSearchParams] = useSearchParams();
  const admitItemId = searchParams.get('admit');
  const admitCycleId = searchParams.get('cycle');
  const admitAbove = admitItemId
    ? (showingBands ? [...firm, ...atRisk] : above).find((s) => s.priority.id === admitItemId) ?? null
    : null;
  const admitBlockedReason: string | null = !admitItemId
    ? null
    : admitCycleId && cycleId !== admitCycleId
      ? 'The scenario open here is not linked to that plan. Switch to the scenario linked to it to answer “what comes off?”.'
      : !planOutput
        ? 'Run the engine to answer “what comes off?” — the question needs this run’s numbers.'
        : !disciplineOn
          ? null
          : !admitAbove
            ? 'That commitment is not above the line in these results, so nothing has to come off for it.'
            : commitmentStates && !commitmentStates.has(admitItemId)
              ? 'That commitment has no resource basis on the record yet, so there is nothing to weigh against it.'
              : null;
  const canAsk = askWhatComesOff != null;
  useEffect(() => {
    if (!admitItemId || !canAsk || !admitAbove || admitBlockedReason) return;
    if (!commitmentStates?.has(admitItemId)) return;
    setCeremonyFor(admitAbove);
    const next = new URLSearchParams(searchParams);
    next.delete('admit');
    next.delete('cycle');
    setSearchParams(next, { replace: true });
  }, [admitItemId, canAsk, admitAbove, admitBlockedReason, commitmentStates, searchParams, setSearchParams]);

  return (
    <div className="p-4 md:p-6 w-full h-full overflow-y-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20">
              <TrendingUp className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Above / Below the Line
            </h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] ml-10">
            {!planOutput
              ? 'Leadership view — what is in, what is out, what is at risk'
              : showingBands
                ? `${firm.length} firm · ${atRisk.length} at risk · ${out.length} out · ${PLANNING_MODE_LABELS[planOutput.planningMode]}`
                : `${above.length} above · ${binaryAtRisk.length} at risk · ${below.length} below · ${PLANNING_MODE_LABELS[planOutput.planningMode]}`}
          </p>
          {showCommitmentLine && (
            <div className="ml-10 mt-0.5">
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                {commitments.rollup.committed} backed · {commitments.rollup.announced} announced
                {commitments.rollup.unknown > 0 ? ` · ${commitments.rollup.unknown} with no basis` : ''}
              </p>
              {/* The zero reading is the honest first reading, not a broken
                  feature — say so before anyone has to ask. */}
              {commitments.rollup.committed === 0 && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                  Nothing here has resources bound to it yet
                  {commitments.noDemandTemplates ? (
                    <>
                      {' — and no work type carries a demand template, so there is nothing to measure claims against. Set one in '}
                      <Link
                        to="/prioritization/settings/work-types"
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        Settings → Work Types
                      </Link>
                      .
                    </>
                  ) : (
                    '.'
                  )}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          {planOutput && !probabilityByPriorityId && (
            <Link
              to="/planning/engine/monte-carlo"
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]"
            >
              <Dices className="h-4 w-4" />
              Add confidence
            </Link>
          )}
          {bandsAvailable && (
            <button
              type="button"
              onClick={() => setShowBands((v) => !v)}
              className="px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]"
            >
              {showingBands ? 'One line' : 'Two lines'}
            </button>
          )}
          <button
            onClick={() => void runEngineForScenario()}
            disabled={running || runBlockedReason != null}
            title={runBlockedReason ?? undefined}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            Run Engine
          </button>
        </div>
      </div>

      <ScenarioSummarySentence />

      {admitBlockedReason && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2 text-sm text-[var(--text-pri)]">
          {admitBlockedReason}
        </div>
      )}

      {scopeBanner && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] px-3 py-2 text-sm text-[var(--text-sec)]">
          {scopeBanner}
        </div>
      )}

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {scopeIsNotPlannable ? (
        <NonPlannableScopeNotice />
      ) : !planOutput ? (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-12 text-center">
          <TrendingUp className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No results yet. Run the engine to see what is above and below the planning line.
          </p>
          {activeScenario && (
            <button
              onClick={() => void runEngineForScenario()}
              disabled={running}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium mx-auto"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Run Engine
            </button>
          )}
        </div>
      ) : showingBands ? (
        <>
          <Section
            band="firm"
            rows={firm}
            bandByPriorityId={bandByPriorityId}
            probabilityByPriorityId={probabilityByPriorityId}
            maxRequiredWU={maxRequiredWU}
            caption="carries fully even with the capacity buffer on"
            startRank={1}
            commitmentStates={commitmentStates}
            onAskWhatComesOff={askWhatComesOff}
          />
          <Section
            band="at_risk"
            rows={atRisk}
            bandByPriorityId={bandByPriorityId}
            probabilityByPriorityId={probabilityByPriorityId}
            maxRequiredWU={maxRequiredWU}
            caption="above the line only optimistically, only partially, or with participation unsettled"
            startRank={firm.length + 1}
            commitmentStates={commitmentStates}
            onAskWhatComesOff={askWhatComesOff}
          />
          <Section
            band="out"
            rows={out}
            bandByPriorityId={bandByPriorityId}
            probabilityByPriorityId={probabilityByPriorityId}
            maxRequiredWU={maxRequiredWU}
            caption="does not fit under either pass"
            startRank={firm.length + atRisk.length + 1}
            commitmentStates={commitmentStates}
          />

          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Two passes of the same engine: optimistic (raw estimates, unbuffered pools) and
            conservative (uncertainty-adjusted estimates, pools less the {bufferPct}% buffer).
            Firm = in under both · At risk = in under one, partly in, or participation unconfirmed ·
            Out = in under neither.
            {bands && bands.rankDistanceBasis !== 'none' && (
              <> Row spacing shows rank distance measured from {bands.rankDistanceBasis}; the bar shows commitment size.</>
            )}
          </p>
        </>
      ) : (
        <>
          <Section
            band="firm"
            rows={above}
            bandByPriorityId={null}
            probabilityByPriorityId={probabilityByPriorityId}
            maxRequiredWU={maxRequiredWU}
            caption="Fully or Partially Achievable"
            startRank={1}
            commitmentStates={commitmentStates}
            onAskWhatComesOff={askWhatComesOff}
          />
          <Section
            band="out"
            rows={below}
            bandByPriorityId={null}
            probabilityByPriorityId={probabilityByPriorityId}
            maxRequiredWU={maxRequiredWU}
            caption="Not Achievable, Blocked, Not Ready, or No Work Units Defined"
            startRank={above.length + 1}
            commitmentStates={commitmentStates}
          />
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Above the line = Fully or Partially Achievable · At risk = achievable but cross-functional participation is not fully confirmed ·
            Below the line = Not Achievable, Blocked, Not Ready, or No Work Units Defined
          </p>
        </>
      )}

      {showPoolLedger && (
        <PoolPressureLedger
          optimistic={optimisticPools}
          conservative={conservativePools}
          bufferPct={bufferPct}
          nameOf={(itemId) => nameByItemId.get(itemId) ?? 'Untitled commitment'}
        />
      )}

      {ceremonyFor && ceremonyImpact && ceremonyState && cycleId && (
        <DisplacementCeremonyModal
          planCycleId={cycleId}
          admittedItemId={ceremonyFor.priority.id}
          admittedName={ceremonyFor.priority.name}
          marginalRequirement={ceremonyState.requirements}
          impact={ceremonyImpact}
          candidates={ceremonyCandidates}
          onClose={() => setCeremonyFor(null)}
          onRecorded={() => {
            setCeremonyFor(null);
            void runEngineForScenario();
          }}
        />
      )}
    </div>
  );
}
